import { useTranslation } from "react-i18next";
import { Box, StepConnector, Stepper, styled, Typography } from "@mui/material";

import { Milestone, ServicePayment } from "../../../../../../types/service.type";
import { createContractState } from "../../../../../../types/contractState.types";
import {
  ServiceContractStatusEnum,
  ServiceRequestStatusEnum,
  ServiceRequestTypeEnum,
} from "../../../../../../enum/service.enum";

import { MilestoneTimelineStep } from "./MilestoneTimelineStep";
import { calculateMilestonePrice, calculateActiveStepIndex } from "./utils";

interface MilestoneTimelineProps {
  milestones: Milestone[];
  paymentConfig: ServicePayment;
  isProvider: boolean;
  isOwnershipLoading: boolean;
  contractId: number;
  contractType: ServiceRequestTypeEnum;
  contractStatus: ServiceContractStatusEnum;
  serviceRequestStatus: ServiceRequestStatusEnum;
  serviceRequestType: ServiceRequestTypeEnum;
}

export const MilestoneTimeline = ({
  milestones,
  paymentConfig,
  isProvider,
  isOwnershipLoading,
  contractId,
  contractType,
  contractStatus,
  serviceRequestStatus,
  serviceRequestType,
}: MilestoneTimelineProps) => {
  const { t } = useTranslation("goService", { keyPrefix: "serviceRequestPage" });

  const CustomStepConnector = styled(StepConnector)(() => styles.stepConnector);

  const activeStepIndex = calculateActiveStepIndex(milestones);
  const milestonePrice = calculateMilestonePrice(paymentConfig, contractType, milestones.length);

  // Create contract state for business logic calculations
  const contractState: ReturnType<typeof createContractState> = {
    contractStatus,
    serviceRequestStatus,
    serviceRequestType,
    milestones,
    paymentData: null,
  };

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>{t("milestoneTimeline")}</Typography>
      <Stepper
        sx={{ height: "fit-content" }}
        activeStep={activeStepIndex}
        orientation="vertical"
        connector={<CustomStepConnector />}
      >
        {milestones.map((milestone, idx) => (
          <MilestoneTimelineStep
            key={milestone.id || idx}
            milestone={milestone}
            contractState={contractState}
            milestoneIndex={idx + 1}
            milestonePrice={milestonePrice}
            isLast={idx === milestones.length - 1}
            isProvider={isProvider}
            isOwnershipLoading={isOwnershipLoading}
            contractId={contractId}
          />
        ))}
      </Stepper>
    </Box>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  title: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "20px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "30px",
  },
  stepConnector: {
    "&.MuiStepConnector-root": {
      marginLeft: 15,
      display: "none",
      "&:not(:first-of-type)": {
        display: "block",
      },
    },
    "&.Mui-active, &.Mui-completed": {
      "& .MuiStepConnector-line": {
        borderColor: "var(--Green---100, #008B07)",
        borderLeftWidth: "2px",
      },
    },
    "& .MuiStepConnector-line": {
      borderColor: "var(--Gray-200, #EAECF0)",
      minHeight: "32px",
      borderLeftWidth: "2px",
    },
  },
};
